import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  CreditCard, Plus, Edit2, Trash2, Pause, Play, Search,
  Calendar, DollarSign, Zap, AlertTriangle, ExternalLink, User
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { apiFetch, API } from "@/utils/api";

// Meses por ciclo de facturacion — fuente unica en frontend (espejo de backend/utils/subscriptions.py).
const BILLING_MONTHS = { monthly: 1, bimonthly: 2, quarterly: 3, semiannual: 6, annual: 12, biennial: 24 };
const CYCLE_LABELS = {
  monthly: "Mensual", bimonthly: "Bimestral", quarterly: "Trimestral",
  semiannual: "Semestral", annual: "Anual", biennial: "Bienal (2 años)",
};

const subscriptionMonthlyCost = (s) => {
  const price = s?.price || 0;
  const months = BILLING_MONTHS[s?.billing_cycle] || 1;
  return Math.round((price / months) * 100) / 100;
};

export default function Subscriptions() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [filterChannel, setFilterChannel] = useState("all");

  const [form, setForm] = useState({
    name: "", provider: "", channel: "both", billing_cycle: "monthly",
    price: 0, currency: "EUR", renewal_date: "", payment_url: "",
    account_holder: "", notes: ""
  });

  const fetchSubs = useCallback(async () => {
    try {
      const res = await apiFetch(`${API}/crm/subscriptions`);
      if (res.ok) setSubs(await res.json());
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSubs(); }, [fetchSubs]);

  const handleSave = async () => {
    const method = editing ? "PUT" : "POST";
    const url = editing ? `${API}/crm/subscriptions/${editing.subscription_id}` : `${API}/crm/subscriptions`;
    const payload = { ...form, price: parseFloat(form.price) || 0 };
    try {
      const res = await apiFetch(url, {
        method,
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success(editing ? "Suscripcion actualizada" : "Suscripcion creada");
        setShowForm(false);
        setEditing(null);
        resetForm();
        fetchSubs();
      } else {
        let err = null;
        try { err = await res.json(); } catch { /* no body */ }
        console.error("[Subscriptions] save failed", res.status, err);
        let msg = `Error ${res.status}`;
        if (err?.detail) {
          if (typeof err.detail === "string") msg = err.detail;
          else if (Array.isArray(err.detail)) msg = err.detail.map(e => `${(e.loc || []).join(".")}: ${e.msg}`).join(" | ");
          else msg = JSON.stringify(err.detail);
        }
        toast.error(msg);
      }
    } catch (e) {
      console.error("[Subscriptions] save error", e);
      toast.error("Error de conexion");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar suscripcion?")) return;
    try {
      const res = await apiFetch(`${API}/crm/subscriptions/${id}`, { method: "DELETE" });
      if (res.ok) { toast.success("Eliminada"); fetchSubs(); }
    } catch (e) { toast.error("Error"); }
  };

  const handleStatusChange = async (id, status) => {
    try {
      const res = await apiFetch(`${API}/crm/subscriptions/${id}/status?status=${status}`, { method: "PUT" });
      if (res.ok) { toast.success("Estado actualizado"); fetchSubs(); }
    } catch (e) { toast.error("Error"); }
  };

  const resetForm = () => setForm({ name: "", provider: "", channel: "both", billing_cycle: "monthly", price: 0, currency: "EUR", renewal_date: "", payment_url: "", account_holder: "", notes: "" });

  const handleConfirmRenewal = async (id) => {
    try {
      const res = await apiFetch(`${API}/crm/subscriptions/${id}/confirm-renewal`, { method: "PUT" });
      if (res.ok) { toast.success("Renovacion marcada como verificada"); fetchSubs(); }
      else { toast.error("No se pudo verificar"); }
    } catch (e) { toast.error("Error de conexion"); }
  };

  const filtered = subs.filter(s => {
    const matchSearch = s.name.toLowerCase().includes(search.toLowerCase()) || (s.provider || "").toLowerCase().includes(search.toLowerCase());
    const matchChannel = filterChannel === "all" || s.channel === filterChannel;
    return matchSearch && matchChannel;
  });

  const totalMonthlyCost = subs.filter(s => s.status === "active").reduce((sum, s) => {
    return sum + subscriptionMonthlyCost(s);
  }, 0);

  const channelColors = {
    linkedin: "bg-blue-50 text-blue-700 border-blue-200",
    mail: "bg-purple-50 text-purple-700 border-purple-200",
    both: "bg-slate-100 text-slate-700 border-slate-200",
  };

  const statusColors = {
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    cancelled: "bg-red-50 text-red-600 border-red-200",
    paused: "bg-amber-50 text-amber-700 border-amber-200",
  };

  const daysUntilRenewal = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    const now = new Date();
    return Math.ceil((d - now) / (1000 * 60 * 60 * 24));
  };

  const renewalUrgencyClass = (days) => {
    if (days === null || days < 0) return "";
    if (days <= 7) return "text-red-600 font-semibold";
    if (days <= 14) return "text-amber-600 font-medium";
    return "";
  };

  const displayedSubs = filtered;

  return (
    <div className="space-y-6" data-testid="subscriptions-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold text-slate-900 tracking-tight" data-testid="subscriptions-title">Suscripciones</h1>
          <p className="text-sm text-slate-500 mt-1">Recordatorio de renovaciones. La imputación de costes se hace desde facturas recibidas.</p>
        </div>
        <Button
          data-testid="add-subscription-btn"
          onClick={() => { setEditing(null); resetForm(); setShowForm(true); }}
          className="bg-slate-900 hover:bg-slate-800 text-white"
        >
          <Plus className="w-4 h-4 mr-2" /> Nueva Suscripcion
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4 text-slate-400" />
            <span className="text-xs text-slate-500 uppercase font-medium">Coste Mensual Total</span>
          </div>
          <p className="font-mono text-2xl font-semibold text-slate-900" data-testid="monthly-cost">{totalMonthlyCost.toLocaleString("es-ES", { minimumFractionDigits: 2 })} EUR</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <Zap className="w-4 h-4 text-slate-400" />
            <span className="text-xs text-slate-500 uppercase font-medium">Activas</span>
          </div>
          <p className="font-mono text-2xl font-semibold text-slate-900" data-testid="active-count">{subs.filter(s => s.status === "active").length}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="w-4 h-4 text-slate-400" />
            <span className="text-xs text-slate-500 uppercase font-medium">Proxima Renovacion</span>
          </div>
          <p className="font-mono text-2xl font-semibold text-slate-900" data-testid="next-renewal">
            {subs.filter(s => s.status === "active" && s.renewal_date).sort((a, b) => a.renewal_date?.localeCompare(b.renewal_date))[0]?.renewal_date?.slice(0, 10) || "---"}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input data-testid="sub-search" placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" />
        </div>
        <Select value={filterChannel} onValueChange={setFilterChannel}>
          <SelectTrigger className="w-40" data-testid="channel-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los canales</SelectItem>
            <SelectItem value="linkedin">LinkedIn</SelectItem>
            <SelectItem value="mail">Mail</SelectItem>
            <SelectItem value="both">Ambos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">Cargando...</div>
      ) : displayedSubs.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-slate-200">
          <CreditCard className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No hay suscripciones</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="subscriptions-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-xs text-slate-500 uppercase tracking-wider">
                <th className="px-4 py-2.5 text-left font-semibold">Herramienta</th>
                <th className="px-4 py-2.5 text-left font-semibold">Canal</th>
                <th className="px-4 py-2.5 text-left font-semibold">Ciclo</th>
                <th className="px-4 py-2.5 text-right font-semibold">Precio</th>
                <th className="px-4 py-2.5 text-left font-semibold">Renovacion</th>
                <th className="px-4 py-2.5 text-left font-semibold">Cuenta</th>
                <th className="px-4 py-2.5 text-center font-semibold">Estado</th>
                <th className="px-4 py-2.5 text-right font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {displayedSubs.map(s => {
                const days = daysUntilRenewal(s.renewal_date);
                return (
                  <tr key={s.subscription_id} className="border-b border-slate-100 hover:bg-slate-50/80 transition-colors" data-testid={`sub-row-${s.subscription_id}`}>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{s.name}</span>
                        {s.provider && <span className="text-xs text-slate-400">{s.provider}</span>}
                        {s.payment_url && (
                          <a
                            href={s.payment_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1 rounded hover:bg-blue-50 text-blue-600"
                            title="Abrir ajustes de pago"
                            data-testid={`payment-url-${s.subscription_id}`}
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${channelColors[s.channel] || "bg-slate-100"}`}>
                        {s.channel === "linkedin" ? "LinkedIn" : s.channel === "mail" ? "Mail" : "Ambos"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs capitalize">{CYCLE_LABELS[s.billing_cycle] || s.billing_cycle}</td>
                    <td className="px-4 py-2.5 font-mono text-right">{s.price?.toLocaleString("es-ES", { minimumFractionDigits: 2 })} {s.currency}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className={`font-mono text-xs ${renewalUrgencyClass(days)}`}>
                          {s.renewal_date?.slice(0, 10) || "---"}
                        </span>
                        {days !== null && days >= 0 && days <= 14 && (
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            days <= 7
                              ? "bg-red-100 text-red-700"
                              : "bg-amber-100 text-amber-700"
                          }`}>
                            {days}d
                          </span>
                        )}
                        {s.renewal_needs_review && (
                          <button
                            onClick={() => handleConfirmRenewal(s.subscription_id)}
                            className="p-0.5 rounded hover:bg-amber-100 text-amber-600"
                            title="Fecha autopospuesta por el sistema. Verifica la renovacion real en la plataforma y haz click aqui para marcarla como verificada."
                            data-testid={`review-renewal-${s.subscription_id}`}
                          >
                            <AlertTriangle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      {s.account_holder ? (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                          <User className="w-3 h-3 text-slate-400" />
                          {s.account_holder}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${statusColors[s.status] || "bg-slate-100"}`}>
                        {s.status === "active" ? "Activa" : s.status === "cancelled" ? "Cancelada" : "Pausada"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex justify-end gap-1">
                        {s.status === "active" ? (
                          <button data-testid={`pause-sub-${s.subscription_id}`} onClick={() => handleStatusChange(s.subscription_id, "paused")} className="p-1.5 rounded hover:bg-amber-50" title="Pausar"><Pause className="w-3.5 h-3.5 text-amber-500" /></button>
                        ) : s.status === "paused" ? (
                          <button data-testid={`activate-sub-${s.subscription_id}`} onClick={() => handleStatusChange(s.subscription_id, "active")} className="p-1.5 rounded hover:bg-emerald-50" title="Activar"><Play className="w-3.5 h-3.5 text-emerald-500" /></button>
                        ) : null}
                        <button data-testid={`edit-sub-${s.subscription_id}`} onClick={() => {
                          setEditing(s);
                          setForm({
                            name: s.name, provider: s.provider || "", channel: s.channel,
                            billing_cycle: s.billing_cycle, price: s.price, currency: s.currency,
                            renewal_date: s.renewal_date?.slice(0, 10) || "",
                            payment_url: s.payment_url || "",
                            account_holder: s.account_holder || "",
                            notes: s.notes || ""
                          });
                          setShowForm(true);
                        }} className="p-1.5 rounded hover:bg-slate-100"><Edit2 className="w-3.5 h-3.5 text-slate-500" /></button>
                        <button data-testid={`delete-sub-${s.subscription_id}`} onClick={() => handleDelete(s.subscription_id)} className="p-1.5 rounded hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg" data-testid="subscription-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Editar Suscripcion" : "Nueva Suscripcion"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Nombre *</label>
                <Input data-testid="sub-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ej: Apollo, Hunter, Lemlist" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Proveedor</label>
                <Input data-testid="sub-provider-input" value={form.provider} onChange={e => setForm({ ...form, provider: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Canal</label>
                <Select value={form.channel} onValueChange={v => setForm({ ...form, channel: v })}>
                  <SelectTrigger data-testid="sub-channel-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="linkedin">LinkedIn</SelectItem>
                    <SelectItem value="mail">Mail</SelectItem>
                    <SelectItem value="both">Ambos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Ciclo</label>
                <Select value={form.billing_cycle} onValueChange={v => setForm({ ...form, billing_cycle: v })}>
                  <SelectTrigger data-testid="sub-cycle-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(CYCLE_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Precio</label>
                <Input data-testid="sub-price-input" type="number" step="0.01" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Moneda</label>
                <Select value={form.currency} onValueChange={v => setForm({ ...form, currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Renovacion *</label>
                <Input data-testid="sub-renewal-input" type="date" min={new Date().toISOString().split("T")[0]} value={form.renewal_date} onChange={e => setForm({ ...form, renewal_date: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Link ajustes de pago</label>
                <Input
                  data-testid="sub-payment-url-input"
                  type="url"
                  placeholder="https://..."
                  value={form.payment_url}
                  onChange={e => setForm({ ...form, payment_url: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Titular de la cuenta</label>
                <Input
                  data-testid="sub-account-holder-input"
                  placeholder="Ej: Jorge, Adrian..."
                  value={form.account_holder}
                  onChange={e => setForm({ ...form, account_holder: e.target.value })}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Notas</label>
              <textarea data-testid="sub-notes-input" className="w-full border border-slate-200 rounded-md p-2 text-sm" rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button data-testid="save-sub-btn" onClick={handleSave} disabled={!form.name || !form.renewal_date} className="bg-slate-900 hover:bg-slate-800 text-white">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
